import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { DefaultStockType } from 'types/schedule'
import { AccordionCheckbox, AccordionItem, AccordionWrapper } from 'components/Accordion'
import { FormLabel } from 'components/Label'
import { Spacer } from 'components/Utility'
import { Headline, Description, SubDescription } from 'components/Typography'
import { useStringChangeEvent } from 'hooks/form'
import { useStockAmountOptions } from 'hooks/stock'
import { SelectBox, Textarea } from 'components/Form'
import { FirebaseDocRefType } from 'firebase'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { StockUnionType } from 'types/stock'
import DefaultMultiDefaultStockForm from 'components/Form/DefaultMultiDefaultStockForm'
import DefaultChoiceFormWrapper from 'components/Form/DefaultChoiceFormWrapper'
import { StoreDefaultScheduleType } from 'types/storeDefault'

type Props = {
  addDefaultStock: (ref: FirebaseDocRefType, type: StockUnionType) => void
  addSchedule: (type: StockUnionType) => void
  deleteDefaultStock: (ref: FirebaseDocRefType, defaultStockIndex: number, type: StockUnionType) => void
  deleteSchedule: (index: number, type: StockUnionType) => void
  handleDefaultStocks: (
    defaultStock: DefaultStockType,
    index: number,
    scheduleRef?: FirebaseDocRefType,
    type?: StockUnionType
  ) => void
  handleHoursToDeadline: (dateToDeadline: string, timeToDeadline: string, ref?: FirebaseDocRefType) => void
  isChecked: boolean
  label: string
  schedules: StoreDefaultScheduleType[]
  selectDaysOfWeek: (dayOfWeek: DayOfWeekType, index: number, type: StockUnionType | '') => void
  selectAllDaysOfWeek: (daysOfWeek: DayOfWeekType[], index: number, type: StockUnionType | '') => void
  setPreorderSchedules: Dispatch<SetStateAction<StoreDefaultScheduleType[]>>
  unselectAllDaysOfWeek: (index: number, type: StockUnionType | '') => void
  selectMaximumOrder: (value: string, type: StockUnionType) => void
  toggleCheckbox: () => void
  type: StockUnionType
  updateNote: (event: React.FocusEvent<HTMLTextAreaElement>, type: StockUnionType) => void
}

const ProductDefaultPreorderSchedule: FC<Props> = (props) => {
  const {
    addDefaultStock,
    addSchedule,
    deleteDefaultStock,
    deleteSchedule,
    handleDefaultStocks,
    handleHoursToDeadline,
    isChecked,
    label,
    schedules,
    selectDaysOfWeek,
    selectMaximumOrder,
    selectAllDaysOfWeek,
    setPreorderSchedules,
    unselectAllDaysOfWeek,
    toggleCheckbox,
    type,
    updateNote,
  } = props

  const [note, setNote] = useState<string>('')
  const [isInit, setInit] = useState<boolean>(false)

  const handleNote = useStringChangeEvent(setNote)
  const maximumOrderOptions = useStockAmountOptions()

  // schedulesの初期値を設定する（2回目以降の更新はスキップ）
  useEffect(() => {
    if (schedules.length === 0) return
    const schedule = schedules[0]
    // 商品受渡し時の注意点が設定されていれば更新
    if (schedule.note !== '' && !isInit) {
      setNote(schedule.note)
      setInit(true)
    }
  }, [schedules])

  return (
    <AccordionWrapper>
      <AccordionCheckbox id={type} label={label} isChecked={isChecked} toggleCheckbox={toggleCheckbox}>
        <AccordionItem>
          <Spacer size={16} />
          <Headline headline={`事前予約日の設定`} />
          <Spacer size={16} />
          <Description description={'特定の日だけ販売数を変更するなど、後から編集も可能です。'} />
          <Spacer size={28} />
          <FormLabel id={`${type}-stock`} label={'受渡解禁時刻と1日の販売上限数'} required={true} />
          <Spacer size={8} />
          <SubDescription description={'商品を受け渡しできる時刻と1日の販売上限数を設定してください。'} />
          <Spacer size={12} />
          <DefaultMultiDefaultStockForm
            addDefaultStock={addDefaultStock}
            addSchedule={addSchedule}
            deleteDefaultStock={deleteDefaultStock}
            deleteSchedule={deleteSchedule}
            handleDefaultStocks={handleDefaultStocks}
            handleHoursToDeadline={handleHoursToDeadline}
            schedules={schedules}
            selectDaysOfWeek={selectDaysOfWeek}
            selectAllDaysOfWeek={selectAllDaysOfWeek}
            unselectAllDaysOfWeek={unselectAllDaysOfWeek}
            type={type}
          />
          <Spacer size={28} />
          <FormLabel label={'おひとりさま購入上限数'} required={true} />
          <Spacer size={8} />
          <SubDescription description={'お客さまが、1回の注文で購入できる上限を設定してください。'} />
          <Spacer size={8} />
          <SelectBox
            options={maximumOrderOptions}
            select={(value) => selectMaximumOrder(value, type)}
            value={schedules[0] ? String(schedules[0].maximum_order) : '0'}
          />
          <Spacer size={28} />
          <FormLabel id={`${type}-choices`} label={'商品の受渡しオプション'} required={false} />
          <Spacer size={8} />
          <SubDescription
            description={'食パンのスライスなど、お客さまが購入する際に選択できるオプションがある場合設定してください。'}
          />
          <Spacer size={8} />
          <DefaultChoiceFormWrapper
            choices={schedules[0] ? schedules[0].choices : []}
            setSchedules={setPreorderSchedules}
            type={type}
          />
          <Spacer size={28} />
          <Textarea
            disabled={false}
            label={'商品受渡し時の注意点'}
            maxLength={280}
            onBlur={(event) => updateNote(event, type)}
            onChange={handleNote}
            placeholder={
              '(記入例)\n手持ち袋は有料になりますので、エコバッグをお持ち下さい。\nポイントカードをお持ちの方は受け取り時にお声がけください。'
            }
            required={false}
            rows={16}
            value={note}
          />
          <Spacer size={16} />
        </AccordionItem>
      </AccordionCheckbox>
    </AccordionWrapper>
  )
}

export default ProductDefaultPreorderSchedule
